import React, { useState } from 'react';
import styled from 'styled-components';
import { Container } from '../styles/components';
import SectionNumber from './SectionNumber';

const SectionWrapper = styled.section`
  height: calc(100svh - 80px);
  max-height: 100svh;
  padding: 100px 0 0 0;
  position: relative;
  background: ${props => props.theme.colors.charcoal};
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    height: auto;
    max-height: none;
    overflow: visible;
    padding-bottom: ${props => props.theme.spacing.xxl};
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: url('/assets/patterns/Contours - clipped (dark).svg');
    background-size: cover;
    background-position: center center;
    background-repeat: no-repeat;
    opacity: 1;
    z-index: 1;
  }
`;

const ContentContainer = styled(Container)`
  max-width: 100%;
  margin: 0;
  padding: 0;
  position: relative;
  z-index: 2;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const HeaderWrapper = styled.div`
  text-align: center;
  margin-bottom: ${props => props.theme.spacing.sm};
  padding: 0 ${props => props.theme.spacing.lg};
  flex-shrink: 0;
`;

const SectionHeading = styled.h2`
  font-family: ${props => props.theme.typography.heading.fontFamily};
  font-weight: ${props => props.theme.typography.heading.fontWeight};
  font-size: 2.5rem;
  line-height: 1.1;
  color: ${props => props.theme.colors.bone};
  margin: 0 0 ${props => props.theme.spacing.md} 0;
`;

const SubHeading = styled.p`
  font-family: ${props => props.theme.typography.body.fontFamily};
  font-size: ${props => props.theme.typography.sizes.bodyLarge};
  color: ${props => props.theme.colors.bone};
  margin: 0;
`;

const ScrollContainer = styled.div`
  flex: 1;
  overflow-x: auto;
  overflow-y: hidden;
  display: flex;
  align-items: center;
  padding: 0 ${props => props.theme.spacing.xxl};
  gap: 0; /* No gap because arrows will take up space or be part of layout */
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    flex-direction: column;
    overflow-y: auto;
    overflow-x: hidden;
    padding: ${props => props.theme.spacing.xl} ${props => props.theme.spacing.md};
    gap: ${props => props.theme.spacing.xl};
    height: auto;
    scroll-snap-type: none;
  }
`;

const NodeWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  scroll-snap-align: center;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    width: 100%;
    justify-content: center;
    margin-bottom: ${props => props.theme.spacing.xl};
  }
`;

const ArrowConnector = styled.div`
  width: 60px;
  height: 2px;
  background: ${props => props.theme.colors.bone};
  position: relative;
  opacity: 0.3;
  margin: 0 20px;

  &::after {
    content: '';
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 0; 
    height: 0; 
    border-top: 5px solid transparent;
    border-bottom: 5px solid transparent;
    border-left: 8px solid ${props => props.theme.colors.bone};
  }

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    display: none; 
  }
`;

// 3D Flip Card Components
const CardScene = styled.div`
  width: 400px;
  height: 520px;
  perspective: 1000px;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    width: 100%;
    height: 480px; 
    margin-bottom: ${props => props.theme.spacing.lg};
  }
`;

const CardFace = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  backface-visibility: hidden;
  border-radius: 24px;
  overflow: hidden;
  background: ${props => props.theme.colors.backgroundAlt};
  box-shadow: 0 10px 40px -10px rgba(0,0,0,0.08); /* Soft shadow */
  border: 1px solid rgba(0,0,0,0.08); /* Soft border */
  display: flex;
  flex-direction: column;
  transition: transform 0.6s cubic-bezier(0.4, 0.0, 0.2, 1);
`;

const CardFront = styled(CardFace)`
  transform: ${props => props.$isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)'};
  background: ${props => props.theme.colors.bone};
`;

const CardBack = styled(CardFace)`
  transform: ${props => props.$isFlipped ? 'rotateY(0deg)' : 'rotateY(-180deg)'};
  background: ${props => props.theme.colors.bone};
`;

// Front Content
const CardImage = styled.div`
  flex: 1;
  background-color: ${props => props.theme.colors.lightGray}30;
  background-image: url(${props => props.src});
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  background-origin: content-box;
  padding: 1.5rem;
  position: relative;
`;

const FrontHeader = styled.div`
  padding: ${props => props.theme.spacing.xl};
  background: transparent;
  border-top: 1px dashed ${props => props.theme.colors.midGray}40;
`;

const PhaseNumber = styled.div`
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: ${props => props.theme.colors.terracotta};
  font-weight: 700;
  margin-bottom: ${props => props.theme.spacing.xs};
`;

const PhaseTitle = styled.h3`
  font-family: ${props => props.theme.typography.heading.fontFamily};
  font-size: 1.6rem;
  color: ${props => props.theme.colors.charcoal};
  margin: 0 0 ${props => props.theme.spacing.sm} 0;
`;

const PhaseTagline = styled.p`
  margin: 0;
  color: ${props => props.theme.colors.midGray};
  font-style: italic;
  font-size: 1rem;
  line-height: 1.4;
`;

const ActionFooter = styled.div`
  padding: 0 ${props => props.theme.spacing.xl} ${props => props.theme.spacing.xl};
  background: transparent;
  text-align: right;
`;

const RevealButton = styled.button`
  background: transparent;
  border: 1px solid ${props => props.theme.colors.terracotta};
  color: ${props => props.theme.colors.terracotta};
  padding: 8px 16px;
  border-radius: 100px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.colors.terracotta};
    color: #FFF;
  }
`;

// Back Content
const BackHeader = styled.div`
  padding: ${props => props.theme.spacing.lg};
  border-bottom: 1px dashed ${props => props.theme.colors.midGray}40;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: transparent;
`;

const BackTitle = styled.h3`
  font-family: ${props => props.theme.typography.heading.fontFamily};
  font-size: 1.1rem;
  color: ${props => props.theme.colors.charcoal};
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  line-height: 1;
  color: ${props => props.theme.colors.midGray};
  cursor: pointer;
  padding: 0;
  &:hover { color: ${props => props.theme.colors.charcoal}; }
`;

const BackBody = styled.div`
  padding: ${props => props.theme.spacing.lg};
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.lg};
`;

const InputHours = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: ${props => props.theme.colors.gold}15;
  border-radius: 8px;
  color: ${props => props.theme.colors.charcoal};
  font-weight: 600;
  font-size: 0.9rem;

  svg { width: 18px; height: 18px; }
`;

const SectionTitle = styled.h4`
  font-family: ${props => props.theme.typography.heading.fontFamily};
  font-size: 0.95rem;
  color: ${props => props.theme.colors.terracotta};
  margin: 0 0 ${props => props.theme.spacing.sm} 0;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const List = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`;

const ListItem = styled.li`
  position: relative;
  padding-left: 14px;
  margin-bottom: 8px;
  line-height: 1.4;
  color: ${props => props.theme.colors.charcoal};
  font-size: 0.9rem;

  &::before {
    content: '•';
    position: absolute;
    left: 0;
    color: ${props => props.theme.colors.terracotta};
  }
`;

const Icon = ({ name }) => {
  if (name === 'clock') {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"></circle>
        <polyline points="12 6 12 12 16 14"></polyline>
      </svg>
    );
  }
  return null;
};

const ProcessSection = () => {
  const [flippedIndex, setFlippedIndex] = useState(-1);

  const handleFlip = (index) => {
    setFlippedIndex(index === flippedIndex ? -1 : index);
  };

  const phases = [
    {
      title: "Strategy & data dive",
      time: "Week 1-2",
      effort: "~4h input needed",
      tagline: "Mapping the current state of affairs; getting your business out of your head and into mine.",
      image: "/assets/map.png",
      whatHappens: [
        "Discovery calls with key team members",
        "Strategy & proof mapping (12 categories)",
        "Market positioning analysis",
        "Financial model review",
        "Red flags & gap identification"
      ],
      whatYouGet: [
        "Strategic recommendations report",
        "Clear view of what's missing to be fundable"
      ]
    },
    {
      title: "Building the Case",
      time: "Week 2",
      effort: "~3h input needed",
      tagline: "Identifying risks, then building the arguments, proof points and narrative to counter them.",
      image: "/assets/develop.png",
      whatHappens: [
        "Investment case development",
        "Identify key arguments/claims",
        "Assign proof points",
        "Design narrative"
      ],
      whatYouGet: [
        "Argument-led narrative outline",
        "Messaging framework",
        "Rigorous logic structure"
      ]
    },
    {
      title: "Deck Build",
      time: "Week 3-4",
      effort: "~3h input needed",
      tagline: "Design, copy, visual storytelling.",
      image: "/assets/build.png",
      whatHappens: [
        "Slide-by-slide construction",
        "Visual hierarchy & flow optimization",
        "Appendix strategy for Q&A",
        "Brand alignment"
      ],
      whatYouGet: [
        "Investor-ready presentation deck",
        "Answers to questions before they're asked"
      ]
    },
    {
      title: "Refinement",
      time: "Week 4",
      effort: "~2h input needed",
      tagline: "Polish & practice.",
      image: "/assets/deliver.png",
      whatHappens: [
        "Live pitch practice session",
        "Feedback integration & polish",
        "Speaker notes & coaching",
        "LinkedIn / online presence review"
      ],
      whatYouGet: [
        "Final deck",
        "Confidence to deliver your story"
      ]
    }
  ];

  return (
    <SectionWrapper id="process">
      <ContentContainer>
        <HeaderWrapper>
          <SectionHeading>
            How we (re)build your pitch
          </SectionHeading>
          <SubHeading>
            We'll prepare you for the raise, not just the presentation.
          </SubHeading>
        </HeaderWrapper>

        <ScrollContainer>
          {phases.map((phase, index) => (
            <React.Fragment key={index}>
              <NodeWrapper>
                <CardScene>
                  <CardFront $isFlipped={flippedIndex === index}>
                    <CardImage src={phase.image} />
                    <FrontHeader>
                      <PhaseNumber>Step {index + 1}</PhaseNumber>
                      <PhaseTitle>{phase.title}</PhaseTitle>
                      <PhaseTagline>{phase.tagline}</PhaseTagline>
                    </FrontHeader>
                    <ActionFooter>
                      <RevealButton onClick={() => handleFlip(index)}>
                        View Details
                      </RevealButton>
                    </ActionFooter>
                  </CardFront>

                  <CardBack $isFlipped={flippedIndex === index}>
                    <BackHeader>
                      <BackTitle>Step {index + 1}: {phase.time}</BackTitle>
                      <CloseButton onClick={() => handleFlip(-1)}>&times;</CloseButton>
                    </BackHeader>
                    <BackBody>

                      <InputHours>
                        <Icon name="clock" /> {phase.effort}
                      </InputHours>

                      <div>
                        <SectionTitle>What Happens</SectionTitle>
                        <List>
                          {phase.whatHappens.map((item, i) => (
                            <ListItem key={i}>{item}</ListItem>
                          ))}
                        </List>
                      </div>

                      <div>
                        <SectionTitle>What You Get</SectionTitle>
                        <List>
                          {phase.whatYouGet.map((item, i) => (
                            <ListItem key={i}>{item}</ListItem>
                          ))}
                        </List>
                      </div>
                    </BackBody>
                  </CardBack>
                </CardScene>
              </NodeWrapper>

              {index < phases.length - 1 && <ArrowConnector />}
            </React.Fragment>
          ))}
          {/* Spacer for end spacing */}
          <div style={{ minWidth: '40px' }} />
        </ScrollContainer>
      </ContentContainer>

      <SectionNumber number={4} />
    </SectionWrapper>
  );
};

export default ProcessSection;
